import { newInternalLogger as internalLogger } from '../../../../services/loggerService/newInternalLogger';
import { _getLoggerOptions } from '../../../../services/loggerService/utils/LocalOptions';
import { decoratorUtil } from './decorators.util';

/**
 * D8 method decorator for logging method execution
 * Uses Proxy with Reflect.apply for enhanced "this" context handling
 * Works with both synchronous and asynchronous methods
 * @example
 * // Basic usage without options
 * @d8()
 * syncMethod() {
 *   // Sync method code
 * }
 *
 * @example
 * // With custom debug message
 * @d8({
 *   message: 'Custom debug message'
 * })
 * async asyncMethod() {
 *   // Async method code
 * }
 *
 * @param options Configuration options
 * @param options.message Optional message to include in the debug log
 * @returns Method decorator
 */

export function d8(options: { message?: string } = {}): any {
  return function (
    target: any,
    propertyKey: string | symbol,
    descriptor?: PropertyDescriptor
  ): void {
    // Handle case where descriptor might be undefined (for static methods)
    let needsDefineProperty = false;
    if (!descriptor) {
      descriptor = decoratorUtil.getOwnPropertyDescriptor(target, propertyKey);
      needsDefineProperty = true;
    }

    // Store the original method implementation
    const originalMethod = descriptor.value;
    const methodName = String(propertyKey);
    const className = decoratorUtil.getClassName(target);

    // Replace the original method with a Proxy that intercepts calls
    descriptor.value = new Proxy(originalMethod, {
      apply(target, thisArg, args) {
        const enableDecorators = _getLoggerOptions()?.enableDecorators;
        if (!enableDecorators || !internalLogger?.debug?.d8) {
          return Reflect.apply(target, thisArg, args);
        }

        internalLogger.debug.d8({
          className: className,
          methodName: methodName,
          message:
            options.message || `${className}.${methodName} not implemented`
        });

        return Reflect.apply(target, thisArg, args);
      }
    });

    // Define property for fallback descriptor if needed
    if (needsDefineProperty) {
      Object.defineProperty(target, propertyKey, descriptor);
    }
  };
}
